import { useRef, useState, Suspense, lazy } from "react";
import { InfoIcon, CloseIcon } from "../icons";
import { HelpIcon } from "../icons/HelpIcon";

import { useUnviewedFeatures } from "./hooks";
import { Tooltip, TooltipTrigger } from "@artis/ui";
import { SidebarIconWrapper } from "../layout/SidebarIconWrapper";
import { ModalSheet } from "../components/ModalSheet";
import { useModal } from "../components/Modal";
import { useNewFeatures } from "../../sanity/queries";
import { AnnouncementBody } from "./Announcement";
import { KeyboardShortcuts } from "./KeyboardShortcuts";
import { useDiagnosticsModal } from "../diagnostics/Diagnostics";
import { useContactUsModal } from "../components/hooks/useContactUsModal";
import { Toggle } from "../ui/toggle";
import { Button } from "../ui/button";
import { useNotificationCentreEnabled } from "../alerts/feature-flag";

const LazyPortal = lazy(() => import("./InfoPortal"));

export const InfoPopup = () => {
  const [position, setPosition] = useState<null | {
    top: number;
    left: number;
  }>(null);
  const ref = useRef<HTMLButtonElement>(null);
  const notificationCentreEnabled = useNotificationCentreEnabled();
  const unviewed = useUnviewedFeatures(3);
  const features = useNewFeatures();
  const diagnosticsModal = useDiagnosticsModal();
  const contactUsModal = useContactUsModal(diagnosticsModal);

  const announcementModal = useModal((p: { id: string }) => {
    const feature = features.data?.allNewFeature.find((x) => x._id === p.id);
    return (
      <ModalSheet
        title={feature?.title ?? ""}
        onClose={() => announcementModal.close()}
        closeButtonVariant="primary"
        showHeaderCloseIcon={true}
      >
        <div className="overflow-y-auto max-h-[80vh] text-foreground-muted">
          <AnnouncementBody bodyRaw={feature?.bodyRaw} />
        </div>
      </ModalSheet>
    );
  });

  const [showMacKeys, setShowMacKeys] = useState(false);

  const keyboardShortcutsModal = useModal(() => {
    return (
      <ModalSheet
        onClose={() => keyboardShortcutsModal.close()}
        closeButtonVariant="primary"
        showHeaderCloseIcon={true}
        footer={
          <div className="flex justify-between items-center mt-2">
            <div className="flex items-center gap-3">
              <span className="text-foreground font-medium">MacOs</span>
              <Toggle checked={showMacKeys} onChange={(e) => setShowMacKeys(e.target.checked)} />
            </div>
            <Button onClick={() => keyboardShortcutsModal.close()} buttonType="closeSecondary" className="font-medium">
              Close
            </Button>
          </div>
        }
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-foreground">Keyboard Shortcuts</h3>
          <button
            type="button"
            onClick={() => keyboardShortcutsModal.close()}
            className="cursor-pointer bg-transparent border-0 p-1 text-[var(--neutral-default)]"
          >
            <CloseIcon />
          </button>
        </div>
        <div className="overflow-y-auto max-h-[80vh] text-foreground inline-flex flex-col justify-center items-center min-w-[400px] max-w-[550px] p-[18px] gap-[15px]">
          <KeyboardShortcuts showMacKeys={showMacKeys} />
        </div>
      </ModalSheet>
    );
  });

  return (
    <>
      <TooltipTrigger placement="right">
        <SidebarIconWrapper>
          <button
            ref={ref}
            type="button"
            data-testid="info-button"
            className="flex items-center justify-center w-full h-full"
            onClick={() => {
              if (!ref.current) return;
              if (notificationCentreEnabled && position) {
                setPosition(null);
              } else {
                const { bottom, left } = ref.current.getBoundingClientRect();
                setPosition({ top: bottom, left });
              }
            }}
          >
            {notificationCentreEnabled ? (
              <HelpIcon />
            ) : (
              <InfoIcon notifications={unviewed?.some((u) => u.viewed !== true)} />
            )}
          </button>
        </SidebarIconWrapper>
        <Tooltip>{notificationCentreEnabled ? "Help" : "Info"}</Tooltip>
      </TooltipTrigger>
      {position && (
        // TODO: would be nice to have a loading spinner here!
        <Suspense>
          <LazyPortal
            announcementModal={notificationCentreEnabled ? undefined : announcementModal.open}
            contactUsModal={contactUsModal.open}
            keyboardShortcutsModal={keyboardShortcutsModal.open}
            position={position}
            setPosition={setPosition}
            triggerRef={ref}
          />
        </Suspense>
      )}
    </>
  );
};
